// ConvertContactPage.tsx — geführte Umwandlung eines Kontakts zwischen Firma
// und Person, Route `/contacts/:id/convert/:target`.
//
// Zeigt dieselbe Maske wie die Anlage (NewPrivateContactForm bzw.
// NewCompanyContactForm), nur VORAUSGEFÜLLT aus dem bestehenden Kontakt. Am
// Ende steht kein POST, sondern:
//   1. POST /contact/:id/convert   — Typ umhängen, typ-fremde Felder
//      archivieren + leeren, Beziehungen typgerecht umschreiben (Transaktion)
//   2. PUT  /contact/:id           — die im Formular geänderten/ergänzten Felder
//
// Diese Reihenfolge ist wichtig: der Convert leert die typ-fremden Felder, das
// anschließende PUT schreibt die neuen Werte. Umgekehrt würde der Convert die
// gerade eingegebenen Angaben wieder wegräumen.

import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import NewPrivateContactForm from "../components/NewPrivateContactForm";
import NewCompanyContactForm from "../components/NewCompanyContactForm";
import { apiFetch } from "../utils/apiFetch";
import { apiUrl } from "../utils/api";
import {
  contactToPrivateFormState,
  contactToCompanyFormState,
} from "../utils/contactToFormState";
import { contactToRelationItems, flattenRelations } from "../utils/contactToRelationItems";
import { istHerkunftBeziehung } from "../data/herkunftBeziehungstypen";
import { buildPrivateContactCreatePayload } from "../utils/buildPrivateContactCreatePayload";
import { buildCompanyContactCreatePayload } from "../utils/buildCompanyContactCreatePayload";

// Derselbe State-Typ wie die Anlage-Masken (utils/formUtils).
import type { FormState } from "../utils/formUtils";
type ConvertTarget = "Firma" | "Person";

interface PreflightRelation {
  relationId: number;
  otherName: string;
  relationType: string;
  function: string | null;
}
interface Preflight {
  current: ConvertTarget;
  target: ConvertTarget;
  blockers: string[];
  fieldsToClear: { column: string; label: string; value: string }[];
  relationsLosingRole: PreflightRelation[];
  lastModified: string | null;
}

/**
 * Felder, die der Anlage-Payload setzt, beim UPDATE eines bestehenden Kontakts
 * aber nichts zu suchen haben: Typ (setzt der Convert), Kundennummer/Identität
 * und die Ownership-Felder (die strippt das Backend im PUT ohnehin, sie würden
 * hier nur Rauschen erzeugen).
 */
const str = (v: unknown): string => (v == null ? "" : String(v));

const stripForUpdate = (payload: Record<string, unknown>): Record<string, unknown> => {
  const {
    type: _type,
    foUserId: _fo,
    boType: _bt,
    boUserId: _bu,
    boTeamId: _btm,
    ...rest
  } = payload;
  return rest;
};

export default function ConvertContactPage() {
  const { id, target } = useParams<{ id: string; target: string }>();
  const navigate = useNavigate();
  const contactId = Number(id);
  const to: ConvertTarget = target === "Firma" ? "Firma" : "Person";

  const [contact, setContact] = useState<Record<string, any> | null>(null);
  const [pre, setPre] = useState<Preflight | null>(null);
  const [formFields, setFormFields] = useState<FormState>({});
  const [error, setError] = useState<string | null>(null);
  // Verantwortlichkeit beim Laden — nur bei echter Änderung wird sie geschrieben.
  const [ownership0, setOwnership0] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!Number.isFinite(contactId)) return;
    let aborted = false;
    setLoading(true);
    Promise.all([
      apiFetch<Record<string, any>>(`contact/${contactId}`, {
        params: { include: "relations,relations-deep" },
      }),
      apiFetch<Preflight>(`contact/${contactId}/convert-preflight`, {
        params: { target: to },
      }),
    ])
      .then(([c, p]) => {
        if (aborted) return;
        const data = c.data ?? null;
        setContact(data);
        setPre(p.data ?? null);
        if (data) {
          const base =
            to === "Person"
              ? contactToPrivateFormState(data)
              : contactToCompanyFormState(data);
          // Bestehende Beziehungen in die Sektionen der Anlage übersetzen, damit
          // sie vorausgefüllt und editierbar erscheinen.
          const rel = contactToRelationItems(data, to);
          // Ausgangsbestand merken: was beim Speichern fehlt, wurde in der Maske
          // entfernt und wird gelöscht. Die Beschriftungen kommen mit, damit der
          // Bestätigungsdialog die entfallenden Beziehungen benennen kann —
          // danach sind sie nicht mehr abfragbar.
          // Herkunft-Verknüpfungen (Empfehlungsgeber/Tippgeber, T-203) stehen nicht
          // in der Maske — sie dürfen deshalb auch nicht in den Ausgangsbestand,
          // sonst würden sie beim Speichern als „entfernt" gelöscht.
          const flat = flattenRelations(data).filter((f) => !istHerkunftBeziehung(f));
          const previousIds = flat
            .map((f) => f.relationId)
            .filter((n): n is number => typeof n === "number");
          const previousLabels: Record<string, string> = {};
          for (const f of flat) {
            if (typeof f.relationId !== "number") continue;
            const name =
              `${f.other?.lastname ?? ""}${f.other?.firstName ? ", " + f.other.firstName : ""}`.trim() ||
              "Unbenannter Kontakt";
            previousLabels[String(f.relationId)] = f.relType ? `${name} — ${f.relType}` : name;
          }
          const initial = {
            ...base,
            rel_household: JSON.stringify(rel.rel_household),
            rel_weitere: JSON.stringify(rel.rel_weitere),
            rel_personFirma: JSON.stringify(rel.rel_personFirma),
            rel_menschen: JSON.stringify(rel.rel_menschen),
            rel_firmaConn: JSON.stringify(rel.rel_firmaConn),
            rel_kvGroupId: rel.rel_kvGroupId,
            rel_previousIds: JSON.stringify(previousIds),
            rel_previousLabels: JSON.stringify(previousLabels),
          };
          setFormFields(initial);
          // Aus `base` gelesen, nicht aus `initial`: `initial` ueberschreibt nur die
          // rel_*-Schluessel, die Ownership-Felder stammen unveraendert aus `base`.
          // (`base` traegt die Index-Signatur von `contactTo*FormState`; beim Spread
          // in ein Objektliteral geht sie verloren, `initial.foUserId` waere daher
          // kein bekanntes Feld mehr.)
          setOwnership0(
            [base.foUserId, base.boType, base.boUserId, base.boTeamId].join("|"),
          );
        }
      })
      .catch((e) => {
        if (!aborted) setError(String(e?.message ?? e));
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [contactId, to]);

  const updateField = (name: string, value: string) =>
    setFormFields((prev) => ({ ...prev, [name]: value }));

  const relationsWithoutRole = useMemo(
    () =>
      (pre?.relationsLosingRole ?? []).map((r) => ({
        name: r.otherName,
        previous: r.function ? `${r.relationType}, ${r.function}` : r.relationType,
      })),
    [pre],
  );

  /**
   * Umwandeln und speichern. Gibt die Kontakt-id zurück, damit das Formular
   * seinen bestehenden Erfolgs-Pfad (Foto-Upload, Navigation) weiterverwenden
   * kann — nur die Beziehungs-Persistenz überspringt es im Convert-Modus.
   */
  const handleSave = async (): Promise<number | false> => {
    if (saving) return false;
    setSaving(true);
    setError(null);
    try {
      // 1. Typ umwandeln (transaktional im Backend).
      const res = await fetch(apiUrl(`contact/${contactId}/convert`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ target: to, lastModified: pre?.lastModified ?? null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setError(body?.message ?? "Die Umwandlung ist fehlgeschlagen.");
        return false;
      }

      // 2. Die im Formular gepflegten Felder schreiben.
      //
      // `allowIncomplete`: Die Vollständigkeitsprüfung der Builder gilt der
      // NEUANLAGE (kein Kontakt ohne Nachname bzw. keine Firma ohne Rechtsform).
      // Hier wird ein BESTEHENDER Kontakt aktualisiert — und beim Umwandeln
      // fehlt der Zielwert prinzipbedingt: Eine Person hat keine Rechtsform.
      // Ohne das Flag käme `null` zurück, das Speichern würde übersprungen und
      // der Anwender bekäme trotzdem eine Erfolgsmeldung.
      const payload =
        to === "Person"
          ? buildPrivateContactCreatePayload(formFields as any, undefined, {}, { allowIncomplete: true })
          : buildCompanyContactCreatePayload(formFields as any, undefined, { allowIncomplete: true })
              ?.payloadWithDetails;
      if (!payload) {
        // Darf mit `allowIncomplete` nicht mehr vorkommen. Sollte es doch je
        // eintreten, ist Schweigen die schlechteste Antwort: Der Typ ist dann
        // bereits umgewandelt, die Eingaben aber nicht geschrieben.
        setError(
          "Der Typ wurde umgewandelt, die Angaben konnten aber nicht übernommen werden. Bitte auf der Kontaktseite prüfen.",
        );
        return false;
      }
      const put = await fetch(apiUrl(`contact/${contactId}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(stripForUpdate(payload as Record<string, unknown>)),
      });
      if (!put.ok) {
        setError(
          "Der Typ wurde umgewandelt, die Angaben konnten aber nicht gespeichert werden. Bitte auf der Kontaktseite prüfen.",
        );
        return false;
      }
      // 3. Verantwortlichkeit: das PUT strippt FO/BO bewusst (Mass-Assignment).
      //    Wurde sie im Formular geändert, über den dafür vorgesehenen Endpoint
      //    schreiben — sonst ginge die Eingabe still verloren.
      const fo = str(formFields.foUserId);
      const boType = str(formFields.boType) || "NONE";
      const boUserId = str(formFields.boUserId);
      const boTeamId = str(formFields.boTeamId);
      if ([fo, boType, boUserId, boTeamId].join("|") !== ownership0) {
        const bo =
          boType === "TEAM"
            ? { type: "TEAM", teamId: boTeamId }
            : boType === "USER"
              ? { type: "USER", userId: boUserId }
              : { type: "NONE" };
        await fetch(apiUrl(`contact/${contactId}/ownership`), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(fo ? { foUserId: fo, bo } : { bo }),
        }).catch(() => {
          /* Die Umwandlung selbst ist durch — Verantwortlichkeit notfalls manuell. */
        });
      }

      return contactId;
    } catch (e: unknown) {
      setError(String((e as { message?: string })?.message ?? e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => navigate(`/contacts/${contactId}`);

  if (loading) {
    return <div style={{ padding: 32, color: "#6b5a66" }}>Kontakt wird geladen …</div>;
  }
  if (!contact) {
    return (
      <div style={{ padding: 32 }}>
        <p style={{ color: "#b3261e", fontWeight: 600 }}>
          {error ?? "Kontakt nicht gefunden."}
        </p>
        <button type="button" onClick={goBack}>Zurück</button>
      </div>
    );
  }
  // Blocker aus dem Preflight: die Umwandlung ist hier gar nicht erst möglich.
  if (pre && pre.blockers.length > 0) {
    return (
      <div style={{ padding: 32, maxWidth: 640 }}>
        <h1 style={{ font: "700 20px/1.3 var(--ui, sans-serif)" }}>
          Umwandlung nicht möglich
        </h1>
        <ul style={{ color: "#7a2018" }}>
          {pre.blockers.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
        <button type="button" onClick={goBack}>Zurück zum Kontakt</button>
      </div>
    );
  }

  const from: ConvertTarget = pre?.current ?? (to === "Person" ? "Firma" : "Person");
  const convertProps = {
    from,
    contactId,
    relationsWithoutRole,
    fieldsToClear: (pre?.fieldsToClear ?? []).map((f) => ({
      label: f.label,
      value: f.value,
    })),
  };

  return (
    <>
      {error && (
        <div style={{ padding: "12px 32px", background: "#fdeeec", color: "#b3261e", fontWeight: 600 }}>
          {error}
        </div>
      )}
      {to === "Person" ? (
        <NewPrivateContactForm
          formFields={formFields}
          updateField={updateField}
          onSave={handleSave}
          onSaveSuccess={goBack}
          onClose={goBack}
          convert={convertProps}
        />
      ) : (
        <NewCompanyContactForm
          formFields={formFields}
          updateField={updateField}
          onSave={handleSave}
          onSaveSuccess={goBack}
          onClose={goBack}
          convert={convertProps}
        />
      )}
    </>
  );
}
